import { useContext, useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import { Button, DashboardModule } from '@domain-group/suit-theme'

import {
  digObject, matchFilterString, sortArrayBy, toggleArray,
} from '@domain-group/javascript-utils'

import { useFilters } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useSystem from '@hooks/useSystem'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import type { TemplateQualityControlCategoryModel } from '@models/types'

import { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CategoryListItem from './CategoryListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
  ]
}

type UseSystemPayload = ReturnType<typeof useSystem>

type DeleteItemKeyParams = {
  qualityControlCategory: TemplateQualityControlCategoryModel,
  qualityControlCategories: TemplateQualityControlCategoryModel[],
  options: object,
  updateFn: UseSystemPayload['callbacks']['updateSystem'],
  updateSystem: (payload: HandleCallbackActionParams) => Promise<object>,
}

const deleteCategory = (params: DeleteItemKeyParams) => {
  const {
    qualityControlCategory, qualityControlCategories, options, updateFn, updateSystem,
  } = params

  const updatedCategories = toggleArray(qualityControlCategories, qualityControlCategory, { deepCompare: true })

  const updatedSystem = {
    options: {
      ...options,
      template_qc_categories: updatedCategories,
    },
  }

  const payload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: updatedSystem,
    toastText: 'QC Category Deleted',
  }

  updateSystem(payload)
}

const TemplateQualityControlCategories = (props: { systemPayload: UseSystemPayload }) => {
  const { systemPayload } = props

  const {
    callbacks: { createOrEditQualityControlCategory, updateSystem: updateFn },
    system: { cache_key, id, options },
  } = systemPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: { updateSystem },
  } = pageContext

  const filterPayload = useFilters()
  const {
    // @ts-expect-error TS2339
    pageFilters: { string },
  } = filterPayload

  const qualityControlCategories: TemplateQualityControlCategoryModel[] = digObject(
    options,
    'template_qc_categories',
    [],
  )
  const hasCategories = !!qualityControlCategories.length

  const filteredQualityControlCategories = useMemo(() => {
    const filtered = qualityControlCategories.filter(({ title }) => matchFilterString(title, string))
    return sortArrayBy(filtered, 'asc', 'title')
  }, [cache_key, id, string])

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      title="Template QC Categories"
    >
      <DashboardModuleFilters filterFields={buildPageFilterFields()} {...filterPayload} />

      {!hasCategories && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Template Quality Control Categories present.
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please adjust your filters or add a category.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {hasCategories && (
        // @ts-expect-error TS2322
        <DashboardModule.ScrollableSection flexDirection="column">
          {filteredQualityControlCategories.map((qualityControlCategory: TemplateQualityControlCategoryModel) => (
            <CategoryListItem
              callbacks={{
                deleteCategory: () => deleteCategory({
                  qualityControlCategory,
                  qualityControlCategories,
                  options,
                  updateFn,
                  updateSystem,
                }),
                createOrEditQualityControlCategory,
              }}
              key={qualityControlCategory.id}
              qualityControlCategory={qualityControlCategory}
            />
          ))}
        </DashboardModule.ScrollableSection>
      )}

      <DashboardModule.Footer>
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => createOrEditQualityControlCategory()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add QC Category
        </Button>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default TemplateQualityControlCategories
